import React, { useState, useEffect } from 'react';
import api from '../api';
import ConfirmDeleteModal from './ConfirmDeleteModal';

const AdminSettings: React.FC = () => {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [truncating, setTruncating] = useState(false);
  const [showTruncateConfirm, setShowTruncateConfirm] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<any>(null);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/api/settings/');
      setSettings(res.data || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleSetting = async (key: string, currentValue: string) => {
    const newValue = currentValue === 'true' ? 'false' : 'true';
    setSaving(true);
    try {
      await api.put(`/api/settings/${key}`, { value: newValue });
      setSettings({ ...settings, [key]: newValue });
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to update setting');
    } finally { setSaving(false); }
  };

  const updateSetting = async (key: string, value: string) => {
    setSaving(true);
    try {
      await api.put(`/api/settings/${key}`, { value });
      setSettings({ ...settings, [key]: value });
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to update setting');
    } finally { setSaving(false); }
  };

  const handleTruncateAuditLogs = async () => {
    setShowTruncateConfirm(true);
  };

  const handleTruncateConfirm = async () => {
    setShowTruncateConfirm(false);
    setTruncating(true);
    try {
      const res = await api.delete('/audit-logs/truncate');
      alert(res.data.message || 'Audit logs truncated');
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to truncate audit logs');
    } finally { setTruncating(false); }
  };

  const handleCleanupPreview = async () => {
    setCleaningUp(true);
    setCleanupResult(null);
    try {
      const res = await api.post('/api/admin/cleanup-test-data', { sku_pattern: 'TEST', dry_run: true });
      setCleanupResult(res.data);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Preview failed');
    } finally { setCleaningUp(false); }
  };

  const handleCleanupConfirm = async () => {
    setShowCleanupConfirm(false);
    setCleaningUp(true);
    setCleanupResult(null);
    try {
      const res = await api.post('/api/admin/cleanup-test-data', { sku_pattern: 'TEST', dry_run: false });
      setCleanupResult(res.data);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Cleanup failed');
    } finally { setCleaningUp(false); }
  };

  if (loading) return null;

  const auditEnabled = settings['audit_log_enabled'] === 'true';
  const auditLevel = settings['audit_log_level'] || 'info';

  return (
    <div style={{ padding: '24px 32px', borderBottom: '1px solid #e8ecf0' }}>
      <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', color: '#1e293b' }}>System Settings</h3>
      
      {/* Audit Logging Toggle */}
      <div style={{ padding: '16px', background: '#fafbfc', borderRadius: '8px', border: '1px solid #e8ecf0', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: '#1e293b' }}>Audit Logging</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
              Log user actions to the audit trail. When disabled, no new entries are written.
            </div>
          </div>
          <button
            onClick={() => toggleSetting('audit_log_enabled', settings['audit_log_enabled'] || 'false')}
            disabled={saving}
            style={{
              width: '48px',
              height: '26px',
              borderRadius: '13px',
              border: 'none',
              cursor: 'pointer',
              position: 'relative',
              transition: 'background 0.2s',
              background: auditEnabled ? '#0d9488' : '#cbd5e1',
            }}
          >
            <div style={{
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              background: 'white',
              position: 'absolute',
              top: '2px',
              left: auditEnabled ? '24px' : '2px',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
        </div>

        {/* Severity Level Selector - shown when audit logging is enabled */}
        {auditEnabled && (
          <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #e8ecf0' }}>
            <div style={{ fontSize: '13px', fontWeight: 500, color: '#1e293b', marginBottom: '8px' }}>
              Minimum Severity Level
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>
              Only actions at or above this severity will be logged.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[
                { value: 'info', label: 'Info', desc: 'All actions (views, imports)', color: '#3b82f6' },
                { value: 'warning', label: 'Warning', desc: 'Creates and above', color: '#f59e0b' },
                { value: 'critical', label: 'Critical', desc: 'Updates and deletes only', color: '#ef4444' },
              ].map(level => (
                <button
                  key={level.value}
                  onClick={() => updateSetting('audit_log_level', level.value)}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: auditLevel === level.value ? `2px solid ${level.color}` : '1px solid #e2e8f0',
                    background: auditLevel === level.value ? `${level.color}10` : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 600, color: auditLevel === level.value ? level.color : '#475569' }}>
                    {level.label}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                    {level.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Truncate Audit Logs */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 500, color: '#991b1b' }}>Truncate Audit Logs</div>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
            Permanently delete all audit log entries. This cannot be undone.
          </div>
        </div>
        <button
          onClick={handleTruncateAuditLogs}
          disabled={truncating}
          className="btn btn-danger"
          style={{ fontSize: '12px', padding: '6px 14px', whiteSpace: 'nowrap' }}
        >
          {truncating ? 'Deleting...' : 'Truncate All'}
        </button>
      </div>
      <ConfirmDeleteModal
        isOpen={showTruncateConfirm}
        title="Delete All Audit Logs"
        message="Are you sure you want to delete ALL audit logs? This action cannot be undone."
        requireTypedConfirmation={true}
        confirmationWord="DELETE ALL"
        confirmLabel="Delete All"
        onConfirm={handleTruncateConfirm}
        onCancel={() => setShowTruncateConfirm(false)}
      />

      {/* Cleanup Test Data */}
      <div style={{ padding: '16px', background: '#fff7ed', borderRadius: '8px', border: '1px solid #fed7aa', marginTop: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: '#9a3412' }}>Cleanup Test Data</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
              Remove all orders, job orders, GRNs, movements, adjustments, transfers, and inventory for products with "TEST" in the SKU.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleCleanupPreview}
              disabled={cleaningUp}
              style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}
            >
              Preview
            </button>
            <button
              onClick={() => setShowCleanupConfirm(true)}
              disabled={cleaningUp}
              className="btn btn-danger"
              style={{ fontSize: '12px', padding: '6px 14px', whiteSpace: 'nowrap' }}
            >
              {cleaningUp ? 'Cleaning...' : 'Cleanup Test Data'}
            </button>
          </div>
        </div>
        {cleanupResult && (
          <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '6px', background: 'white', border: '1px solid #e2e8f0', fontSize: '12px' }}>
            <div style={{ fontWeight: 600, marginBottom: '6px', color: '#1e293b' }}>{cleanupResult.message}</div>
            {cleanupResult.products_affected && (
              <div style={{ color: '#64748b', marginBottom: '4px' }}>Products: {cleanupResult.products_affected.join(', ')}</div>
            )}
            {cleanupResult.deleted && (
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', color: '#475569' }}>
                {Object.entries(cleanupResult.deleted || cleanupResult.would_delete || {}).map(([key, val]) => (
                  <span key={key}>{key.replace(/_/g, ' ')}: <strong>{String(val)}</strong></span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDeleteModal
        isOpen={showCleanupConfirm}
        title="Cleanup Test Data"
        message="This will permanently delete ALL transactional data (orders, job orders, GRNs, stock movements, inventory) for products with 'TEST' in the SKU. This cannot be undone."
        requireTypedConfirmation={true}
        confirmationWord="CLEANUP"
        confirmLabel="Cleanup"
        onConfirm={handleCleanupConfirm}
        onCancel={() => setShowCleanupConfirm(false)}
      />
    </div>
  );
};

export default AdminSettings;
